import { ActionType, ProColumns } from '@ant-design/pro-components';
import { DatePicker, Spin } from 'antd';
import CustomBarActivities from 'base/ui/components/CustomChart/BarChart/barChartActivities';
import CustomProTable, { tablePagination } from 'base/ui/components/CustomTable';
import dayjs from 'dayjs';
import usePartialState from 'hooks/usePartialState';
import { cloneDeep } from 'lodash';
import { observer } from 'mobx-react';
import { SearchModel } from 'pages/model';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivitiesOfCustomerSegment } from 'services/Main/customerSegment_services';
import { customerSegmentPresenter, customerSegmentStore } from 'stores/root_store';
import { PAGE_SIZE_DEFAULT } from 'utils/constants';

type ActivitiesTabProps = {
  customerSegmentationId: string;
  customerSegmentationName: string;
};

// TAB HOẠT ĐỘNG
function ActivitiesTab(props: ActivitiesTabProps) {
  const { customerSegmentationId, customerSegmentationName } = props;
  const { t } = useTranslation();

  const [columns, setColumns] = useState<ProColumns<ActivitiesOfCustomerSegment>[]>([
    // stt
    {
      title: t('zalo-notification.stt'),
      dataIndex: 'stt',
      key: 'stt',
      fixed: 'left',
      width: 50,
      align: 'center',
    },
    // Mã CRM (ID hoạt động)
    {
      title: 'ID hoạt động',
      dataIndex: 'crmCode',
      key: 'crmCode',
      fixed: 'left',
      width: 120,
    },
    // Tiêu đề
    {
      title: t('segment.tieu-de'),
      dataIndex: 'title',
      key: 'title',
      fixed: 'left',
      width: 250,
    },
    // Mô tả
    {
      title: t('segment.mo-ta'),
      dataIndex: 'description',
      key: 'description',
      width: 150,
    },
    // Loại
    {
      title: t('segment.loai'),
      dataIndex: 'type',
      key: 'type',
      width: 150,
    },
    // Trạng thái
    {
      title: t('segment.trang-thai'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
    },
    //   Người tạo
    {
      title: t('segment.nguoi-tao'),
      dataIndex: 'e',
      key: 'e',
      width: 100,
    },
    //   Ngày tạo
    {
      title: t('segment.ngay-tao'),
      dataIndex: 'createTime',
      key: 'createTime',
      width: 150,
      valueType: 'dateTime',
      fieldProps: {
        format: 'DD/MM/YYYY HH:mm:ss',
      },
      onCell: (_, index) => ({
        className: 'text-center',
      }),
    },
    //  Mã KH
    {
      title: t('segment.ma-kh'),
      dataIndex: 'customerCode',
      key: 'customerCode',
      width: 130,
    },
    //  Tên KH
    {
      title: t('segment.ten-kh'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 350,
    },
    //  Email
    {
      title: t('segment.email'),
      dataIndex: 'email',
      key: 'email',
      width: 180,
    },
    //  Contact
    {
      title: t('segment.lien-he'),
      dataIndex: 'contact',
      key: 'contact',
      width: 150,
    },
  ]);

  // data chart
  const [dataLabel, setDataLabel] = useState<string[]>([]);
  const [dataTotal, setDataTotal] = useState<number[] | undefined>([]);
  // số record chưa hoàn thành
  const [countUnfinish, setCountUnfinish] = useState<number[]>([]);

  const [dataActivityDone, setDataActivityDone] = useState<number[]>([]);

  const [valueYear, setValueYear] = useState(dayjs().format('YYYY'))


  const table1Ref = useRef<ActionType>();
  const table2Ref = useRef<ActionType>();
  const table3Ref = useRef<ActionType>();
  const table4Ref = useRef<ActionType>();

  const [loading, setLoading] = usePartialState({
    barChart: false
  })

  // --------- NHIỆM VỤ ------------------------------------------------------------
  const [dataMission, setDataMission] = useState<ActivitiesOfCustomerSegment[]>([]);
  const [dataPagingMission, setDataPagingMission] = useState<SearchModel>({
    type: 'ACTIVITIES',
    customerSegmentationId: customerSegmentationId,
    year: valueYear,
    paging: {
      pageSize: PAGE_SIZE_DEFAULT,
      pageIndex: 1,
      orderBy: undefined,
      orderByDesc: undefined,
    },
  });
  // số record nhiệm vụ
  const [totalCountMission, setTotalCountMission] = useState(0);

  // --------- THĂM HỎI KHÁCH HÀNG ------------------------------------------------------------
  const [dataTHKH, setDataTHKH] = useState<ActivitiesOfCustomerSegment[]>([]);
  const [dataPagingTHKH, setDataPagingTHKH] = useState<SearchModel>({
    type: 'THKH',
    customerSegmentationId: customerSegmentationId,
    year: valueYear,
    paging: {
      pageSize: PAGE_SIZE_DEFAULT,
      pageIndex: 1,
      orderBy: undefined,
      orderByDesc: undefined,
    },
  });
  // số record nhiệm vụ
  const [totalCountTHKH, setTotalCountTHKH] = useState(0);

  // --------- KHÁCH GHÉ THĂM ------------------------------------------------------------
  const [dataKGT, setDataKGT] = useState<ActivitiesOfCustomerSegment[]>([]);
  const [dataPagingKGT, setDataPagingKGT] = useState<SearchModel>({
    type: 'GT',
    customerSegmentationId: customerSegmentationId,
    year: valueYear,
    paging: {
      pageSize: PAGE_SIZE_DEFAULT,
      pageIndex: 1,
      orderBy: undefined,
      orderByDesc: undefined,
    },
  });
  // số record nhiệm vụ
  const [totalCountKGT, setTotalCountKGT] = useState(0);

  // --------- BẢO HÀNH/XLKN ------------------------------------------------------------
  const [dataBHXLKN, setDataBHXLKN] = useState<ActivitiesOfCustomerSegment[]>([]);
  const [dataPagingBHXLKN, setDataPagingBHXLKN] = useState<SearchModel>({
    type: 'TICKET,TIKECT_MLC',
    customerSegmentationId: customerSegmentationId,
    year: valueYear,
    paging: {
      pageSize: PAGE_SIZE_DEFAULT,
      pageIndex: 1,
      orderBy: undefined,
      orderByDesc: undefined,
    },
  });
  // số record nhiệm vụ
  const [totalCountBHXLKN, setTotalCountBHXLKN] = useState(0);


  // ======= LẤY DATA ===================================================================================================

  // lấy data nhiệm vụ
  const getDataTableMission = async (dataSearch: SearchModel) => {
    await customerSegmentPresenter.getActivitiesOfCustomerSegment(customerSegmentStore, dataSearch);
    if (customerSegmentStore.activitiesOfCustomerSegment.isSuccess) {
      setDataMission(customerSegmentStore.activitiesOfCustomerSegment.data);
      setTotalCountMission(customerSegmentStore.activitiesOfCustomerSegment.paging?.totalCount || 0);
      return customerSegmentStore.activitiesOfCustomerSegment.data;
    }
    return [];
  };

  // lấy data thăm hỏi khách hàng
  const getDataTableTHKH = async (dataSearch: SearchModel) => {
    await customerSegmentPresenter.getActivitiesOfCustomerSegment(customerSegmentStore, dataSearch);
    if (customerSegmentStore.activitiesOfCustomerSegment.isSuccess) {
      setDataTHKH(customerSegmentStore.activitiesOfCustomerSegment.data);
      setTotalCountTHKH(customerSegmentStore.activitiesOfCustomerSegment.paging?.totalCount || 0);
      return customerSegmentStore.activitiesOfCustomerSegment.data;
    }
    return [];
  };

  // lấy data khách ghé thăm
  const getDataTableKGT = async (dataSearch: SearchModel) => {
    await customerSegmentPresenter.getActivitiesOfCustomerSegment(customerSegmentStore, dataSearch);
    if (customerSegmentStore.activitiesOfCustomerSegment.isSuccess) {
      setDataKGT(customerSegmentStore.activitiesOfCustomerSegment.data);
      setTotalCountKGT(customerSegmentStore.activitiesOfCustomerSegment.paging?.totalCount || 0);
      return customerSegmentStore.activitiesOfCustomerSegment.data;
    }
    return [];
  };

  // lấy data Bảo hành/XLKN
  const getDataTableBHXLKN = async (dataSearch: SearchModel) => {
    await customerSegmentPresenter.getActivitiesOfCustomerSegment(customerSegmentStore, dataSearch);
    if (customerSegmentStore.activitiesOfCustomerSegment.isSuccess) {
      setDataBHXLKN(customerSegmentStore.activitiesOfCustomerSegment.data);
      setTotalCountBHXLKN(customerSegmentStore.activitiesOfCustomerSegment.paging?.totalCount || 0);
      setTotalCountBHXLKN(customerSegmentStore.activitiesOfCustomerSegment.paging?.totalCount || 0);
      return customerSegmentStore.activitiesOfCustomerSegment.data;
    }
    return [];
  };


  // const [valueYearPicker, setValueYearPicker] = useState<Dayjs | null>(null)
  // Data chart
  const getDataChart = async () => {
    setLoading({ barChart: true })
    await customerSegmentPresenter.getDataChartActivities(customerSegmentStore, customerSegmentationId, Number(valueYear));
    if (customerSegmentStore.dataChartActivities.isSuccess) {
      const dataAPI = cloneDeep(customerSegmentStore.dataChartActivities.data);
      setDataLabel(dataAPI.map((i) => i.monthCreate));
      setCountUnfinish(dataAPI.map((i) => i.activityUnfinished));
      setDataTotal(dataAPI.map((i) => i.total));
      setDataActivityDone(dataAPI.map((i) => i.activityDone));
    }
    setLoading({ barChart: false })
  };

  useEffect(() => {
    getDataChart();
  }, [valueYear]);

  return (
    <div className="w-full mt-3 flex flex-col gap-default">
      <div className="w-full p-2 rounded-sm shadow-box border-2 border-transparent border-t-primary">

        <div className='flex justify-end'>
          <DatePicker
            defaultValue={dayjs()}
            onChange={(value, dateString) => {
              setValueYear(dateString)


              table1Ref.current?.reload()
              table2Ref.current?.reload()
              table3Ref.current?.reload()
              table4Ref.current?.reload()
            }} picker='year' />
        </div>

        <Spin spinning={loading.barChart}>
          <CustomBarActivities
            color={['#9CD866', '#6CC0DE']}
            yAxisName={false}
            title={t('segment.hd-chart')}

            totalData={dataTotal}
            subTitle={'Customer segment: ' + customerSegmentationName}
            data={[
              {
                name: 'Hoạt động hoàn thành',
                stack: 'total',
                fontSize: 13,
                total: dataActivityDone,
              },
              // {
              //   name: 'Hoạt động chưa hoàn thành',
              //   stack: 'total',
              //   fontSize: 13,
              //   total: countUnfinish,
              // },
            ]}
            label={dataLabel}
            rotateLabel
          // className={'!h-full'}
          />
        </Spin>

      </div>

      {/* --------- NHIỆM VỤ ------------------------------------------------------------ */}
      <div className="w-full p-2 rounded-sm shadow-box border-2 border-transparent border-t-primary">
        <CustomProTable<ActivitiesOfCustomerSegment>
          bordered
          headerTitle={<h2 className="text-sm font-bold uppercase">{t('segment.nhiem-vu')}</h2>}
          columns={columns}
          dataSource={dataMission}
          actionRef={table1Ref}
          rowKey="stt"
          request={async (params = {}, sort, filter) => {
            const data = await getDataTableMission({
              ...dataPagingMission,
              year: Number(valueYear),
              paging: {
                ...dataPagingMission.paging,
                orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                orderByDesc:
                  Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
              },
            });

            if (Object.values(sort).length) {
              setDataPagingMission({
                ...dataPagingMission,
                year: Number(valueYear),
                paging: {
                  ...dataPagingMission.paging,
                  orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                  orderByDesc:
                    Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                },
              });
            }
            return {
              data: data,
              success: customerSegmentStore.activitiesOfCustomerSegment.isSuccess,
            };
          }}
          pagination={{
            ...tablePagination(dataPagingMission, setDataPagingMission),
            total: totalCountMission,
          }}
          options={{
            setting: false,
            density: false,
            fullScreen: true,
            reload: true,
          }}
        />
      </div>

      {/*  --------- THĂM HỎI KHÁCH HÀNG ------------------------------------------------------------ */}
      <div className="w-full p-2 rounded-sm shadow-box border-2 border-transparent border-t-primary">
        <CustomProTable<ActivitiesOfCustomerSegment>
          bordered
          className="w-full"
          headerTitle={<h2 className="text-sm font-bold uppercase">{t('segment.th-kh')}</h2>}
          columns={columns}
          dataSource={dataTHKH}
          actionRef={table2Ref}
          rowKey="stt"
          request={async (params = {}, sort, filter) => {
            const data = await getDataTableTHKH({
              ...dataPagingTHKH,
              year: Number(valueYear),
              paging: {
                ...dataPagingTHKH.paging,
                orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                orderByDesc:
                  Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
              },
            });

            if (Object.values(sort).length) {
              setDataPagingTHKH({
                ...dataPagingTHKH,
                year: Number(valueYear),
                paging: {
                  ...dataPagingTHKH.paging,
                  orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                  orderByDesc:
                    Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                },
              });
            }
            return {
              data: data,
              success: customerSegmentStore.activitiesOfCustomerSegment.isSuccess,
            };
          }}
          pagination={{
            ...tablePagination(dataPagingTHKH, setDataPagingTHKH),
            total: totalCountTHKH,
          }}
          options={{
            setting: false,
            density: false,
            fullScreen: true,
            reload: true,
          }}
        />
      </div>

      {/*  --------- KHÁCH GHÉ THĂM ------------------------------------------------------------ */}
      <div className="w-full p-2 rounded-sm shadow-box border-2 border-transparent border-t-primary">
        <CustomProTable<ActivitiesOfCustomerSegment>
          bordered
          className="w-full"
          headerTitle={<h2 className="text-sm font-bold uppercase">{t('segment.visit-customer')}</h2>}
          columns={columns}
          actionRef={table3Ref}
          dataSource={dataKGT}
          rowKey="stt"
          request={async (params = {}, sort, filter) => {
            const data = await getDataTableKGT({
              ...dataPagingKGT,
              year: Number(valueYear),
              paging: {
                ...dataPagingKGT.paging,
                orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                orderByDesc:
                  Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
              },
            });

            if (Object.values(sort).length) {
              setDataPagingKGT({
                ...dataPagingKGT,
                year: Number(valueYear),
                paging: {
                  ...dataPagingKGT.paging,
                  orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                  orderByDesc:
                    Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                },
              });
            }
            return {
              data: data,
              success: customerSegmentStore.activitiesOfCustomerSegment.isSuccess,
            };
          }}
          pagination={{
            ...tablePagination(dataPagingKGT, setDataPagingKGT),
            total: totalCountKGT,
          }}
          options={{
            setting: false,
            density: false,
            fullScreen: true,
            reload: true,
          }}
        />
      </div>


      {/*  --------- BẢO HÀNH/XLKN ------------------------------------------------------------ */}
      <div className="w-full p-2 rounded-sm shadow-box border-2 border-transparent border-t-primary">
        <CustomProTable<ActivitiesOfCustomerSegment>
          bordered
          className="w-full"
          actionRef={table4Ref}
          headerTitle={<h2 className="text-sm font-bold uppercase">{t('segment.ticket')}</h2>}
          columns={columns}
          dataSource={dataBHXLKN}
          rowKey="stt"
          request={async (params = {}, sort, filter) => {
            const data = await getDataTableBHXLKN({
              ...dataPagingBHXLKN,
              year: Number(valueYear),
              paging: {
                ...dataPagingBHXLKN.paging,
                orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                orderByDesc:
                  Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
              },
            });

            if (Object.values(sort).length) {
              setDataPagingBHXLKN({
                ...dataPagingBHXLKN,
                year: Number(valueYear),
                paging: {
                  ...dataPagingBHXLKN.paging,
                  orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                  orderByDesc:
                    Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                },
              });
            }
            return {
              data: data,
              success: customerSegmentStore.activitiesOfCustomerSegment.isSuccess,
            };
          }}

          pagination={{
            ...tablePagination(dataPagingBHXLKN, setDataPagingBHXLKN),
            total: totalCountBHXLKN,
          }}
          options={{
            setting: false,
            density: false,
            fullScreen: true,
            reload: true,
          }}
        />
      </div>


    </div>
  );
}

export default observer(ActivitiesTab);
